/**
 * 聊天面板 —— SPEC.md §5
 * 主流程：输入 → 思考 → 执行 → (失败自修复 ≤ 2 次)
 */
import { useEffect, useRef, useState, useTransition } from "react";
import { Send, Loader2 } from "lucide-react";
import { useAppStore, newMessageId } from "../store/useAppStore";
import { chat, AIError, AISchemaError, type ChatMessage } from "../lib/aiClient";
import { collectFailures, executeCommands, has3DCommands, hasUser3DIntent, switchTo3D } from "../lib/ggbBridge";
import { buildSystemPrompt, buildRepairMessage, buildFormatRepairMessage } from "../lib/prompts";
import type { GGBAppletApi } from "../types/ggb";
import { MessageBubble } from "./MessageBubble";

const MAX_REPAIR = 2;
const MAX_FORMAT_RETRY = 2;
const HISTORY_WINDOW = 6; // 最近 N 轮发给 AI

export function ChatPanel() {
  const [input, setInput] = useState("");
  const [isPending, startTransition] = useTransition();
  const listRef = useRef<HTMLDivElement>(null);

  const config = useAppStore(s => s.config);
  const domain = useAppStore(s => s.domain);
  const messages = useAppStore(s => s.messages);
  const ggbApi = useAppStore(s => s.ggbApi);
  const setThinking = useAppStore(s => s.setThinking);
  const isThinking = useAppStore(s => s.isThinking);
  const appendMessage = useAppStore(s => s.appendMessage);

  // 自动滚到底部
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isThinking]);

  const clearMessages = useAppStore(s => s.clearMessages);

  // 监听模板库发来的 prompt
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<string>;
      if (typeof ce.detail === "string" && ce.detail.trim()) {
        // 模板触发的：先清空画布 + 聊天记录，避免新旧构造混乱
        ggbApi?.newConstruction();
        clearMessages();
        send(ce.detail.trim());
      }
    };
    window.addEventListener("aiggb:send", handler);
    return () => window.removeEventListener("aiggb:send", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.apiKey, ggbApi]);

  const canSend = !!config?.apiKey && !!ggbApi && !isThinking && input.trim().length > 0;

  const send = (text: string) => {
    if (!config?.apiKey) {
      appendMessage({ id: newMessageId(), role: "error", content: "请先在设置中填入 API Key" });
      return;
    }
    if (!ggbApi) {
      appendMessage({ id: newMessageId(), role: "error", content: "GeoGebra 画布尚未就绪，请稍候" });
      return;
    }

    appendMessage({ id: newMessageId(), role: "user", content: text });

    startTransition(() => {
      void runRound(text);
    });
  };

  const runRound = async (userText: string) => {
    setThinking(true);
    try {
      // ★ 连续对话中保持 applet 实例稳定：只在「需要 3D 而当前仍是 2D」时单向升级。
      //   一旦进入 3D，后续语句（即使是非 3D 的修改/属性指令）也保持在同一 3D 实例，
      //   绝不因新语句销毁当前实例切回 2D——3D applet 同样支持 2D 绘制。
      //   实例的彻底重置只发生在「清空 / 新对话」边界（onClear）。
      const currentMode = useAppStore.getState().ggbAppName;
      const userWants3D = hasUser3DIntent(userText);
      if (userWants3D && currentMode !== "3d") {
        const oldApi = useAppStore.getState().ggbApi;
        switchTo3D();                            // → 触发 GGBCanvas 销毁重建（仅 2D→3D）
        // 等待新的 3D applet 加载完成（最多 15s）
        await waitForAPI(oldApi);
      }

      const systemPrompt = buildSystemPrompt(domain);
      const history = collectHistory(messages, HISTORY_WINDOW);
      const baseMessages: ChatMessage[] = [
        { role: "system", content: systemPrompt },
        ...history,
        { role: "user", content: userText }
      ];

      let response = await chatWithFormatRetry(baseMessages, msg =>
        appendMessage({ id: newMessageId(), role: "error", content: msg })
      );

      // ★ 兜底：AI 返回了 3D 命令但当前仍是 2D → 升级到 3D，并等新 api 就绪
      //   已在 3D 实例中则跳过，保持实例稳定
      if (!userWants3D && has3DCommands(response.commands) && useAppStore.getState().ggbAppName !== "3d") {
        const oldApi = useAppStore.getState().ggbApi;
        switchTo3D();
        await waitForAPI(oldApi);
      }

      // —— [ASK] 反问：AI 需要用户澄清，不执行命令 ——
      if (response.ask) {
        useAppStore.getState().appendAIResponse(response, []);
        return;
      }

      // ★ 实时从 store 取 api：3D 切换后旧的 ggbApi 闭包已过期，
      //   必须用最新的 applet 才能让命令真正作用到 3D 画布上
      const api = useAppStore.getState().ggbApi;
      if (!api) {
        appendMessage({ id: newMessageId(), role: "error", content: "GeoGebra 画布尚未就绪，请稍候" });
        return;
      }

      // ★ 快照：执行前保存画布状态，用于失败回滚
      const snapshot = await takeSnapshot(api);

      let results = executeCommands(api, response.commands);
      useAppStore.getState().appendAIResponse(response, results);

      // —— 失败自修复（命令执行失败） ——
      let attempts = 0;
      while (attempts < MAX_REPAIR) {
        const failures = collectFailures(results);
        if (failures.length === 0) break;
        attempts++;

        // 全部失败且是首次修复 → 回滚到快照，避免半成功状态污染
        if (attempts === 1 && failures.length === results.length && snapshot) {
          await restoreSnapshot(api, snapshot);
          // 回滚后重放已成功的历史命令，仅保留干净的状态
          replayPriorCommands(api);
        }

        // 修复循环中也要实时取最新 api
        const curApi = useAppStore.getState().ggbApi ?? api;
        const existingObjs = curApi?.getAllObjectNames() ?? [];
        const repairMessages: ChatMessage[] = [
          { role: "system", content: systemPrompt },
          ...history,
          { role: "user", content: userText },
          { role: "assistant", content: JSON.stringify(response) },
          { role: "user", content: buildRepairMessage(failures, existingObjs) }
        ];
        response = await chatWithFormatRetry(repairMessages, msg =>
          appendMessage({ id: newMessageId(), role: "error", content: msg })
        );
        if (response.ask) {
          useAppStore.getState().appendAIResponse(response, []);
          return;
        }
        results = executeCommands(useAppStore.getState().ggbApi ?? api, response.commands);
        useAppStore.getState().appendAIResponse(response, results);
      }
    } catch (err) {
      const prefix = err instanceof AISchemaError
        ? "AI 返回格式异常（多次重试后仍失败）"
        : "AI 调用失败";
      const msg = err instanceof Error ? err.message : String(err);
      appendMessage({ id: newMessageId(), role: "error", content: `${prefix}：${msg}` });
    } finally {
      setThinking(false);
    }
  };

  /** 保存当前画布为 base64 快照 */
  const takeSnapshot = (api: GGBAppletApi | null): Promise<string | null> =>
    new Promise(resolve => {
      if (!api) return resolve(null);
      try {
        api.getBase64(data => resolve(data ?? null));
      } catch {
        resolve(null);
      }
    });

  /** 从 base64 快照恢复画布 */
  const restoreSnapshot = (api: GGBAppletApi | null, snapshot: string): Promise<void> =>
    new Promise(resolve => {
      if (!api) return resolve();
      try {
        api.setBase64(snapshot, () => resolve());
      } catch {
        resolve();
      }
    });

  /** 回滚后重放上一轮之前的成功命令 */
  const replayPriorCommands = (api: GGBAppletApi | null) => {
    if (!api) return;
    const all = useAppStore.getState().messages;
    for (const m of all) {
      if (m.role === "assistant") {
        for (const r of m.payload.results) {
          if (r.ok && r.expanded.length > 0) {
            // 只重放 eval 类（构造型）命令
            for (const cmd of r.expanded) {
              if (/^[\w]+\s*=/.test(cmd)) {
                api.evalCommand(cmd);
              }
            }
          }
        }
      }
    }
  };

  /** 等待 GGB applet 重建完成：oldApi 被替换为新 api 后 resolve */
  const waitForAPI = (oldApi: ReturnType<typeof useAppStore.getState>["ggbApi"]) =>
    new Promise<void>(resolve => {
      const start = Date.now();
      const check = () => {
        const api = useAppStore.getState().ggbApi;
        if (api && api !== oldApi && typeof api.evalCommand === "function") {
          console.log("[AiGGB 3D] new API ready after", Date.now() - start, "ms");
          resolve();
          return;
        }
        if (Date.now() - start > 15000) {
          console.warn("[AiGGB 3D] API wait timeout after 15s");
          resolve(); // 超时也继续，避免永久阻塞
          return;
        }
        setTimeout(check, 200);
      };
      check();
    });

  /** 调用 chat；遇到 AISchemaError 则把原始输出与 detail 反馈给 AI 重试，最多 MAX_FORMAT_RETRY 次 */
  const chatWithFormatRetry = async (
    msgs: ChatMessage[],
    notify: (msg: string) => void
  ) => {
    let conv: ChatMessage[] = msgs;
    for (let i = 0; i <= MAX_FORMAT_RETRY; i++) {
      try {
        return await chat(config!, conv);
      } catch (err) {
        if (err instanceof AISchemaError && i < MAX_FORMAT_RETRY) {
          // 给用户一个可见提示，告诉它正在自动重试
          notify(`AI 返回格式异常（${err.message}：${err.detail}），正在自动重试 ${i + 1}/${MAX_FORMAT_RETRY}…`);
          conv = [
            ...conv,
            { role: "assistant", content: err.raw },
            { role: "user", content: buildFormatRepairMessage(err.raw, err.detail) }
          ];
          continue;
        }
        throw err;
      }
    }
    throw new AIError("AI 多次返回格式错误，已放弃");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (canSend) {
        send(input.trim());
        setInput("");
      }
    }
  };

  return (
    <section className="chat-panel">
      <div className="chat-messages" ref={listRef}>
        {messages.length === 0 && (
          <div className="empty-hint">
            <h3>{domain === "physics" ? "物理模式 · 开始动态模拟" : "数学模式 · 开始几何构造"}</h3>
            {domain === "physics" ? (
              <>
                <p>试试：<em>「斜抛运动，v0=20 m/s，仰角 45°」</em></p>
                <p className="muted">支持矢量箭头、受力分析、电场线、单摆、弹簧振子…</p>
              </>
            ) : (
              <>
                <p>试试：<em>「过三点 (0,0) (3,0) (1,2) 画外接圆」</em></p>
                <p className="muted">支持几何构造、函数、圆锥曲线、参数曲线、3D 几何体…</p>
              </>
            )}
            <p className="muted">或点击右上方「模板」一键加载场景。Ctrl+Enter 发送。</p>
          </div>
        )}
        {messages.map(m => (
          <MessageBubble key={m.id} turn={m} />
        ))}
        {(isThinking || isPending) && (
          <div className="thinking">
            <Loader2 size={14} className="spin" /> AI 思考中…
          </div>
        )}
      </div>

      <div className="chat-input-area">
        <textarea
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            !config?.apiKey
              ? "请先在右上角『设置』中配置 API Key…"
              : domain === "physics"
              ? "描述物理场景：如 斜抛 v0=20 仰角 45° / 单摆 L=1 θ0=π/6 …  (Ctrl+Enter 发送)"
              : "描述数学图形：如 外接圆 / sin(kx) / 摆线 / 椭圆 a=3 b=2 / 正方体截面 …  (Ctrl+Enter 发送)"
          }
          rows={3}
          disabled={!config?.apiKey}
        />
        <button
          className="send-btn"
          disabled={!canSend}
          onClick={() => {
            if (canSend) {
              send(input.trim());
              setInput("");
            }
          }}
          title="Ctrl+Enter 发送"
        >
          <Send size={16} />
        </button>
      </div>
    </section>
  );
}

function collectHistory(
  messages: ReturnType<typeof useAppStore.getState>["messages"],
  windowSize: number
): ChatMessage[] {
  // 把历史折叠成 user / assistant，仅保留最近 windowSize 轮，避免上下文爆炸
  const collapsed: ChatMessage[] = [];
  for (const m of messages) {
    if (m.role === "user") collapsed.push({ role: "user", content: m.content });
    else if (m.role === "assistant")
      collapsed.push({
        role: "assistant",
        content: JSON.stringify({
          explanation: m.payload.explanation,
          commands: m.payload.commands
        })
      });
  }
  // 取最后 2*windowSize 条消息（user+assistant 配对）
  return collapsed.slice(-windowSize * 2);
}
